// CRITICAL: Force React to load and initialize FIRST before any other module
import './init-react';

// Now import from React after it's initialized
import { StrictMode, useMemo, type PropsWithChildren } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "swiper/swiper-bundle.css";
import "flatpickr/dist/flatpickr.css";
import "./i18n/config"; // Inicializar i18n
import App from "./App";
import { AppWrapper } from "./components/common/PageMeta";
import { ThemeProvider, useTheme as useAppTheme } from "./context/ThemeContext";
import { StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { HelmetProvider } from 'react-helmet-async';
import { createAppTheme } from "./theme/muiTheme";
import { clearFirestoreCache, shouldClearCache } from "./utils/clearFirestoreCache";
import { checkUserData } from "./utils/checkUserData";
import { GlobalErrorBoundary } from "./components/error/GlobalErrorBoundary";
import { initGA } from "./utils/analytics";
import { reportWebVitals } from "./utils/webVitals";
import { initAppCheck } from "./firebase/appCheck";
import { perf } from "./firebase/performance";
import { Capacitor } from '@capacitor/core';
import { registerSW } from 'virtual:pwa-register';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/firebaseConfig';

// Silenciar logs ruidosos en producción (mantener console.error)
const __platform = Capacitor.getPlatform();
const __isNativePlatform = __platform === 'android' || __platform === 'ios';

if (import.meta.env.PROD && !__isNativePlatform) {
  // eslint-disable-next-line no-console
  console.log = () => {};
  // eslint-disable-next-line no-console
  console.info = () => {};
  // eslint-disable-next-line no-console
  console.warn = () => {};
  // eslint-disable-next-line no-console
  console.debug = () => {};
}

// Exponer utilidades en la consola para debugging (solo DEV)
if (import.meta.env.DEV) {
  (window as any).checkUserData = checkUserData;
  (window as any).clearCache = clearFirestoreCache;
}

if (__isNativePlatform) {
  try {
    (window as any).__auth = auth;
    onAuthStateChanged(auth, (u) => {
      console.log('[Auth][startup] onAuthStateChanged', {
        hasUser: Boolean(u),
        uid: u?.uid,
      });
    });
  } catch {
  }
}

// Inicializar servicios de producción
if (!import.meta.env.DEV) {
  initGA();
  reportWebVitals();
  initAppCheck();
  
  // Performance Monitoring ya inicializado en firebase/performance.ts
}

if (typeof window !== 'undefined') {
  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';
  const platform = Capacitor.getPlatform();
  const isNative = platform === 'android' || platform === 'ios';

  const updateAppHeightVar = () => {
    try {
      const vv = window.visualViewport;
      const h = Math.round((vv && vv.height) ? vv.height : window.innerHeight);
      document.documentElement.style.setProperty('--app-height', `${h}px`);
    } catch {
    }
  };

  try {
    document.documentElement.style.setProperty('--keyboard-height', '0px');
  } catch {
  }

  updateAppHeightVar();
  window.addEventListener('resize', updateAppHeightVar);
  window.visualViewport?.addEventListener('resize', updateAppHeightVar);
  window.visualViewport?.addEventListener('scroll', updateAppHeightVar);

  const initKeyboardRuntimeConfig = async () => {
    if (!isNative || platform !== 'ios') return;
    try {
      const mod: any = await import('@capacitor/keyboard');
      const Keyboard = mod?.Keyboard;
      if (!Keyboard) return;

      try {
        await Keyboard.setResizeMode({ mode: 'none' });
      } catch {
      }

      try {
        await Keyboard.setScroll({ isDisabled: true });
      } catch {
      }

      try {
        Keyboard.addListener('keyboardWillShow', (info: any) => {
          try {
            const h = Math.max(0, Math.round(Number(info?.keyboardHeight || 0)));
            document.documentElement.style.setProperty('--keyboard-height', `${h}px`);
          } catch {
          }
          updateAppHeightVar();
        });
        Keyboard.addListener('keyboardWillHide', () => {
          try {
            document.documentElement.style.setProperty('--keyboard-height', '0px');
          } catch {
          }
          updateAppHeightVar();
        });
      } catch {
      }
    } catch {
    }
  };

  initKeyboardRuntimeConfig();

  if (isNative) {
    const blockedHosts = ['apis.google.com', 'developers.google.com'];
    const originalCreateElement = document.createElement.bind(document);
    document.createElement = ((tagName: any, options?: any) => {
      const el = originalCreateElement(tagName, options) as any;
      if (String(tagName).toLowerCase() === 'script') {
        const originalSetAttribute = el.setAttribute?.bind(el);
        if (originalSetAttribute) {
          el.setAttribute = (name: string, value: string) => {
            if (name === 'src' && typeof value === 'string') {
              try {
                const u = new URL(value, window.location.href);
                if (blockedHosts.includes(u.hostname)) {
                  return;
                }
              } catch {
              }
            }
            return originalSetAttribute(name, value);
          };
        }

        Object.defineProperty(el, 'src', {
          set(v) {
            try {
              const u = new URL(String(v), window.location.href);
              if (blockedHosts.includes(u.hostname)) {
                return;
              }
            } catch {
            }
            try {
              (el as any).setAttribute('src', v);
            } catch {
            }
          },
          get() {
            return el.getAttribute?.('src');
          },
          configurable: true,
        });
      }
      return el;
    }) as any;

    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function (child: any) {
      try {
        const tag = child?.tagName ? String(child.tagName).toLowerCase() : '';
        if (tag === 'script' || tag === 'iframe') {
          const src = child?.src || child?.getAttribute?.('src');
          if (typeof src === 'string' && src) {
            const u = new URL(src, window.location.href);
            if (blockedHosts.includes(u.hostname)) {
              return child;
            }
          }
        }
      } catch {
      }
      return originalAppendChild.call(this, child);
    };
  }

  const clearSwAndCaches = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch {
      }
    }

    // Clear CacheStorage (where Workbox stores precache/runtime caches)
    if (typeof caches !== 'undefined') {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch {
      }
    }
  };

  // In native apps (Capacitor), a SW can persist across app updates and serve stale index/assets.
  // We disable it aggressively to avoid "missing hashed chunk" errors for end users.
  if (isNative) {
    clearSwAndCaches();
  } else if (isLocalhost) {
    clearSwAndCaches();
  }

  // Register PWA SW only on web (never on native or localhost).
  if (!import.meta.env.DEV && !isNative && !isLocalhost) {
    registerSW({
      immediate: false,
    });
  }

  if (false && isLocalhost && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
  }

  const shouldReload = (reason: any) => {
    const msg = String(reason?.message || reason || '');
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('ChunkLoadError')
    );
  };

  const reloadOnceKey = '__chunk_reload_once__';
  const reloadOnce = () => {
    try {
      const already = sessionStorage.getItem(reloadOnceKey);
      if (already) return;
      sessionStorage.setItem(reloadOnceKey, '1');
    } catch {
    }
    try {
      window.dispatchEvent(
        new CustomEvent('app:reload-required', {
          detail: { reason: 'ChunkLoadError / preload error' },
        })
      );
    } catch {
    }
  };

  // In DEV, Vite HMR can transiently trigger preload/unhandledrejection errors.
  // Auto-reloading here can cause infinite refresh loops during development.
  if (!import.meta.env.DEV) {
    window.addEventListener('vite:preloadError', reloadOnce);

    window.addEventListener('unhandledrejection', (ev) => {
      if (shouldReload((ev as any)?.reason)) {
        if (isNative) {
          clearSwAndCaches();
        }
        reloadOnce();
      }
    });
  }
}

function MUIBridge({ children }: PropsWithChildren) {
  const { theme } = useAppTheme();
  const muiTheme = useMemo(() => createAppTheme(theme), [theme]);

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
}

// Función helper para renderizar la aplicación
function renderApp() {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    const w = window as any;
    const existingRoot = w.__reactRoot;
    const root = existingRoot || createRoot(rootElement);
    w.__reactRoot = root;
    root.render(
      <StrictMode>
        <HelmetProvider>
          <GlobalErrorBoundary>
            <ThemeProvider>
              <MUIBridge>
                <AppWrapper>
                  <App />
                </AppWrapper>
              </MUIBridge>
            </ThemeProvider>
          </GlobalErrorBoundary>
        </HelmetProvider>
      </StrictMode>
    );
  }
}

// Limpiar caché de Firestore si es necesario (solo en producción).
// En DEV esto puede provocar recargas constantes y perder trabajo.
if (!import.meta.env.DEV && shouldClearCache()) {
  clearFirestoreCache()
    .then(() => {
      try {
        window.dispatchEvent(
          new CustomEvent('app:reload-required', {
            detail: { reason: 'Firestore cache cleared' },
          })
        );
      } catch {
      }
      renderApp();
    })
    .catch(() => {
      renderApp();
    });
} else {
  renderApp();
}
